import React from 'react';
import clsx from 'clsx';
import { Button, Spinner, Typography } from '@/components/common';
import { SignReviewComparison } from '@/interfaces';
import { DataProvidedCard } from './DataProvidedCard';
import { ProfileInformationCard } from './ProfileInformationCard';

interface Props {
	comparison: SignReviewComparison;
	status: 'loading' | 'error' | 'ready';
	actionsDisabled?: boolean;
	validateDisabled?: boolean;
	invalidateDisabled?: boolean;
	onValidate?: () => void;
	onInvalidate?: () => void;
	onRetry?: () => void;
	hideActions?: boolean;
}

export const SignReviewPanel: React.FC<Props> = ({
	comparison,
	status,
	actionsDisabled = false,
	validateDisabled = false,
	invalidateDisabled = false,
	onValidate,
	onInvalidate,
	onRetry,
	hideActions = false,
}) => {
	const isReady = status === 'ready';
	const hasProfileItems = comparison.profileItems.length > 0;

	return (
		<div className="w-full h-full min-h-0 flex flex-col gap-4">
			{status === 'loading' && (
				<div className="flex justify-center py-10">
					<Spinner />
				</div>
			)}

			{status === 'error' && (
				<div
					role="alert"
					className="border border-grey-100 rounded-2xl p-4 flex flex-col items-start gap-4"
				>
					<Typography type="regular-12" className="text-left">
						We couldn&apos;t load the review data.
					</Typography>
					<Button decoration="line-primary" size="small" onClick={onRetry}>
						Try again
					</Button>
				</div>
			)}

			{isReady && (
				<>
					<div
						className={clsx('min-h-0', hasProfileItems ? 'flex-1' : 'flex-[2]')}
					>
						<DataProvidedCard comparison={comparison} />
					</div>
					{hasProfileItems && (
						<div className="flex-1 min-h-0">
							<ProfileInformationCard profileItems={comparison.profileItems} />
						</div>
					)}
				</>
			)}

			{!hideActions && (
				<div className="shrink-0 flex items-center justify-between gap-2">
					<Button
						decoration="danger-outline"
						size="small"
						onClick={onInvalidate}
						disabled={invalidateDisabled || actionsDisabled || !isReady}
					>
						Invalidate
					</Button>
					<Button
						decoration="primary"
						size="small"
						onClick={onValidate}
						disabled={validateDisabled || actionsDisabled || !isReady}
					>
						Validate
					</Button>
				</div>
			)}
		</div>
	);
};
